import { z } from 'zod';
/**
 * Identifiers for the scheduled (cron) jobs the functions runtime owns.
 * The server-side registry in `apps/functions/src/scheduling/jobs.ts`
 * holds the authoritative metadata (schedule, audit action, manual
 * trigger path); this enum is shared so the web admin page and the
 * server agree on the keys.
 */
export const ScheduledJobName = z.enum([
    'signalRefresh',
    'auditRedact',
    'streamliner',
]);
/**
 * Outcome of the last observed run of a job, derived from the most
 * recent matching `audit/*` entry's severity.
 */
export const ScheduledJobOutcome = z.enum(['ok', 'warn', 'failed', 'unknown']);
/**
 * Snapshot of one scheduled job for the admin Background-Jobs UI.
 * Combines the static registry data (schedule, manual trigger path,
 * description) with audit-derived run history. Returned by
 * `GET /api/admin/schedules`.
 */
export const ScheduledJobStatus = z.object({
    name: ScheduledJobName,
    /** Raw cron expression (5 fields, UTC). */
    schedule: z.string(),
    /** Human-readable rendering of `schedule`. */
    humanReadable: z.string(),
    /** HTTP path the admin client hits to manually trigger this job. */
    manualTriggerPath: z.string(),
    /** Audit action recorded on each run; the same string the admin UI's
     *  audit-explorer filters on. */
    auditAction: z.string(),
    /** ISO timestamp of the most recent observed run, or `null` when the
     *  job has never run on this project. */
    lastRunAt: z.string().nullable(),
    /** Outcome of the last observed run (or `'unknown'` when missing). */
    lastRunOutcome: ScheduledJobOutcome,
    /** Free-form detail snippet from the last audit entry's `detail`
     *  field (e.g. counts of items processed). Capped at ~240 chars
     *  so the UI cell stays readable. */
    lastRunDetail: z.string().optional(),
});
export const ScheduledJobsListResponse = z.object({
    jobs: z.array(ScheduledJobStatus),
    /** Whether Cloud Scheduler is enabled on the deployed project. When
     *  false the cron entries don't actually fire — only manual triggers
     *  do. The admin UI surfaces this so admins know to use "Run now". */
    cloudSchedulerEnabled: z.boolean(),
});
